import { Component, Input, ViewChild } from '@angular/core';
import { AmlTimeService } from "src/app/_api/services/aml-time.service";
import { TimeTypesService } from "src/app/_api/services/time-types.service";
import { TimeFormatPipe } from "src/app/shared/pipes/time-format.pipe";
import { LanguageService } from "src/app/shared/services/language.service";
import { UserService } from "src/app/shared/services/user.service";
import { TimeLogEventHandler } from "./time-log-event.service";
import { AmsNotifierService } from "src/app/shared/services/ams-notifier.service";
import { DataGridComponent } from "src/app/shared/_devextreme/data-grid/data-grid.component";

@Component({
  selector: 'app-time-log',
  templateUrl: './time-log.component.html',
  styleUrls: ['./time-log.component.css']
})
export class TimeLogComponent {
  @Input() height: number;
  @Input() timeLog = [];
  @Input() selectedTimeRecord: any;
  @Input() employees = [];

  @ViewChild(DataGridComponent) dataGrid: DataGridComponent;

  timeTypes = [];

  isBusy: boolean;
  canEdit: boolean;

  constructor(
    public languageService: LanguageService,
    private userService: UserService,
    private timeTypeService: TimeTypesService,
    private amlTimeService: AmlTimeService,
    private timeLogEventHandler: TimeLogEventHandler,
    private notifier: AmsNotifierService) {

    this.setEditPermission();
    this.setSelectableTimeTypes();
    this.employeeCellTemplate = this.employeeCellTemplate.bind(this);
    this.timeTypeCellTemplate = this.timeTypeCellTemplate.bind(this);
  }

  repaintGrid() {
    this.dataGrid.grid.instance.repaint();
  }

  private setEditPermission() {
    const canEditSystemParameterValue = this.userService.account.SystemSettings._292;
    this.canEdit = canEditSystemParameterValue !== null && (canEditSystemParameterValue === "1" || canEditSystemParameterValue.toLowerCase() === "true");
  }

  private async setSelectableTimeTypes() {
    await this.loadTimeTypes();
    const selectableTimeTypesSystemParameterValue = this.userService.account.SystemSettings._294;

    if (selectableTimeTypesSystemParameterValue === null) {
      this.timeTypes = [];
      return;
    }
    
    const selectableTimeTypes = selectableTimeTypesSystemParameterValue.split(",").map(Number);    
    this.timeTypes = this.timeTypes.filter((timeType: any) => selectableTimeTypes.includes(timeType.TimeTypeId));
  }

  onEditorPreparing(e: any) {
    if (e.parentType !== "dataRow" || e.editorName !== "dxTextBox") { return; }
    e.editorOptions.mask = "00:00";
  }

  timeCellTemplate(container: any, options: any) {
    const value = new TimeFormatPipe().transform(options.value);
    container.textContent = value;
  }

  employeeCellTemplate(container: any, options: any) {
    container.textContent = options.data.IsAmlTime ? this.employees?.find(e => e.EmployeeId === options.value)?.EmployeeNumber || "" : "";
  }

  timeTypeCellTemplate(container: any, options: any) {
    container.textContent = options.data.IsAmlTime ? this.timeTypes?.find(tt => tt.TimeTypeId === options.value)?.Code || "" : "";
  }

  onCellPrepared(e: any) {
    if (e.rowType !== "data" || !this.selectedTimeRecord) { return; }
    this.highlightRowIfLinkedToSelectedTimeRecord(e);
    this.hideEditOptionsIfNotAmlTime(e);
  }

  private highlightRowIfLinkedToSelectedTimeRecord(e: any) {
    const sameProjectNumbers = e.data.FromProjectNumber === this.selectedTimeRecord.FromProjectNumber && e.data.ToProjectNumber === this.selectedTimeRecord.ToProjectNumber;
    const sameDateAndCarrier = e.data.Date === this.selectedTimeRecord.Date && e.data.CarrierId === this.selectedTimeRecord.CarrierId;
    
    let isLinked: boolean = false; 

    if(this.selectedTimeRecord.CategoryId == 8 || this.selectedTimeRecord.CategoryId == 7) {
      isLinked = e.data && sameProjectNumbers; 
    }
    else {
      isLinked = e.data && sameDateAndCarrier; 
    }
    
    if (!isLinked) { return; }

    e.cellElement.style.background = "var(--bg-grid-selected-row)";
    if (e.columnIndex !== 0) { return; }
    e.cellElement.style.borderLeft = "0.5rem solid var(--theme-color)";    
  }

  private hideEditOptionsIfNotAmlTime(e: any) {
    if (e.column.command !== "edit" || e.data.IsAmlTime) { return; }
    e.cellElement.children[1].remove();
    e.cellElement.children[0].remove();
  }

  private async loadTimeTypes() {
    this.timeTypes = await this.timeTypeService.getActive().toPromise();
  }

  onInitNewRow(e: any) {
    e.Date = this.selectedTimeRecord?.Date;
    e.IsAmlTime = true;
  }

  async onRowInserting(e: any) {
    const amlTime = e.data;
    
    this.isBusy = true;
    const response = await this.amlTimeService.create(amlTime.Date, amlTime.EmployeeId, amlTime.TimeTypeId, amlTime.StartTimeTommss, amlTime.StopTimeTommss).toPromise();
    this.isBusy = false;

    if (!response.IsSuccess) {
      this.notifier.error(response.ErrorMessage);
      return;
    }

    e.component.cancelEditData();
    this.notifier.success(this.languageService.LNG("Label.SavedOK"));
    this.timeLogEventHandler.notifyTimeAdded(response.Data);
  }

  async onRowUpdating(e: any) {
    const currentAmlTime = { ... e.oldData };
    const amlTime = { ... e.oldData, ... e.newData };
    
    this.isBusy = true;
    const response = await this.amlTimeService.update(amlTime.TimeId, amlTime.Date, amlTime.EmployeeId, amlTime.TimeTypeId, amlTime.StartTimeTommss, amlTime.StopTimeTommss).toPromise();
    this.isBusy = false;

    if (!response.IsSuccess) {
      const recordIndex = this.timeLog.findIndex(t => t.TimeId === currentAmlTime.TimeId);
      this.timeLog[recordIndex] = currentAmlTime;
      this.notifier.error(response.ErrorMessage);
      return;
    }

    e.component.cancelEditData();
    this.notifier.success(this.languageService.LNG("Label.SavedOK"));
    this.timeLogEventHandler.notifyTimeUpdated(response.Data);
  }

  async onRowRemoving(e: any) {
    const timeId = e.data.TimeId;
    
    this.isBusy = true;
    const response = await this.amlTimeService.delete(timeId).toPromise();
    this.isBusy = false;

    if (!response.IsSuccess) {
      this.notifier.error(response.ErrorMessage);
      return;
    }

    e.component.cancelEditData();
    this.notifier.success(this.languageService.LNG("Label.DeletedOK"));
    this.timeLogEventHandler.notifyTimeDeleted(timeId);
  }
}
